import React, { useMemo } from "react";

// Unit-agnostic donut for counting entries (e.g. stock-out actions per department).
const PALETTE = [
  "#7A0F14", "#D4AF37", "#B45309", "#4B7F3D", "#8B5A2B",
  "#1F5460", "#8E4162", "#C0724E", "#5D5FEF", "#7A6C5D",
];

function polar(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}
function arc(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const [x1, y1] = polar(cx, cy, rOuter, startAngle);
  const [x2, y2] = polar(cx, cy, rOuter, endAngle);
  const [x3, y3] = polar(cx, cy, rInner, endAngle);
  const [x4, y4] = polar(cx, cy, rInner, startAngle);
  return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4} Z`;
}

/**
 * data: [{ label, count, subtitle? }]
 */
export default function CountDonut({ data = [], size = 220, unit = "entries", testId, emptyLabel = "No data yet" }) {
  const rows = useMemo(() => {
    const withTotal = (data || [])
      .filter((d) => (d.count || 0) > 0)
      .sort((a, b) => b.count - a.count);
    const grand = withTotal.reduce((s, r) => s + Number(r.count || 0), 0);
    return { rows: withTotal, grand };
  }, [data]);

  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4;
  const rInner = rOuter - 32;

  if (!rows.rows.length) {
    return (
      <div className="flex items-center justify-center text-muted-foreground text-sm" style={{ width: size, height: size }} data-testid={testId}>
        {emptyLabel}
      </div>
    );
  }

  let angle = 0;
  const slices = rows.rows.map((row, i) => {
    const pct = row.count / rows.grand;
    const start = angle;
    const end = angle + pct * 360;
    angle = end;
    const color = PALETTE[i % PALETTE.length];
    if (rows.rows.length === 1) {
      return {
        d: `M ${cx + rOuter} ${cy} A ${rOuter} ${rOuter} 0 1 1 ${cx - rOuter} ${cy} A ${rOuter} ${rOuter} 0 1 1 ${cx + rOuter} ${cy} Z ` +
          `M ${cx + rInner} ${cy} A ${rInner} ${rInner} 0 1 0 ${cx - rInner} ${cy} A ${rInner} ${rInner} 0 1 0 ${cx + rInner} ${cy} Z`,
        color, row, pct,
      };
    }
    return { d: arc(cx, cy, rOuter, rInner, start, end), color, row, pct };
  });

  return (
    <div className="flex flex-col sm:flex-row items-center gap-8" data-testid={testId}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {slices.map((s, i) => (
            <path key={i} d={s.d} fill={s.color} fillRule="evenodd">
              <title>{s.row.label}: {s.row.count} ({(s.pct * 100).toFixed(1)}%)</title>
            </path>
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground">TOTAL</span>
          <span className="font-serif text-2xl mt-1">{rows.grand}</span>
          <span className="text-[10px] text-muted-foreground">{unit}</span>
        </div>
      </div>

      <ul className="space-y-2 text-sm w-full sm:w-auto">
        {slices.map((s, i) => (
          <li key={i} className="flex items-start gap-3 min-w-[240px]">
            <span aria-hidden="true" className="inline-block w-3 h-3 rounded-sm shrink-0 mt-1.5" style={{ backgroundColor: s.color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate font-medium">{s.row.label}</span>
                <span className="font-serif tabular-nums">{s.row.count}</span>
              </div>
              {s.row.subtitle && <div className="text-[11px] text-muted-foreground truncate">{s.row.subtitle}</div>}
            </div>
            <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
              {(s.pct * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
